import API from "@/utils/api"
import { API_PATHS } from "@/constants/api-paths"
import type { PaymentCheckoutRequest } from "@/types/payment"

/** Cryptographically strong payment attempt id (no fallback for missing UUID API). */
export function newPaymentAttemptId(): string {
  try {
    return globalThis.crypto.randomUUID()
  } catch {
    throw new Error("Cannot generate payment_attempt_id in this environment.")
  }
}

/**
 * Starts hosted payment session (`API_PATHS.PAYMENT_CHECKOUT`).
 * Every attempt uses a fresh payment_attempt_id tied to billing_snapshot_id.
 */
export async function postPaymentCheckoutSession(input: {
  billingSnapshotId: string
  paymentAttemptId?: string
  /** When true, interceptor skips generic error toast (caller owns UX). */
  skipErrorToast?: boolean
}): Promise<{ redirectUrl: string; paymentAttemptId: string }> {
  const snap = String(input.billingSnapshotId ?? "").trim()
  if (!snap) {
    throw new Error("Missing billing_snapshot_id for payment")
  }

  const paymentAttemptId = input.paymentAttemptId ?? newPaymentAttemptId()
  if (!String(paymentAttemptId).trim()) {
    throw new Error("Missing payment_attempt_id for payment")
  }

  const body: PaymentCheckoutRequest = {
    billing_snapshot_id: snap,
    payment_attempt_id: paymentAttemptId,
  }

  const response = await API.post(API_PATHS.PAYMENT_CHECKOUT, body, {
    skipErrorToast: Boolean(input.skipErrorToast),
  })
  const data = response?.data?.data as { gateway_session_url?: string } | undefined
  if (!data?.gateway_session_url) {
    throw new Error("Invalid payment session response")
  }

  return {
    redirectUrl: data.gateway_session_url,
    paymentAttemptId,
  }
}

export async function startOrderPaymentSession(params: {
  billingSnapshotId: string
  skipErrorToast?: boolean
}): Promise<{ redirectUrl: string; paymentAttemptId: string }> {
  return postPaymentCheckoutSession({
    billingSnapshotId: params.billingSnapshotId,
    skipErrorToast: params.skipErrorToast,
  })
}
